import {
  ApplicationConfig,
  LOCALE_ID,
  provideZoneChangeDetection,
  APP_INITIALIZER,
  inject,
  runInInjectionContext,
  Injector,
} from '@angular/core';
import {
  RouteReuseStrategy,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { mdTransitionAnimation } from '@ionic/angular';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { provideServiceWorker } from '@angular/service-worker';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { provideRemoteConfig, getRemoteConfig, RemoteConfig } from '@angular/fire/remote-config';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { provideHttpClient, HTTP_INTERCEPTORS, withInterceptorsFromDi } from '@angular/common/http';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import {
  PwaUpdateService,
  UserService,
  ThemeService,
  ConfigService,
  SessionMemberService,
  SessionMessageService,
  WorkspaceMemberService,
  TenantConfigService,
  TenantService,
  WorkspaceDefinitionService,
  WorkspaceInstanceService,
  WorkspaceSessionService,
  OrderService,
  LoadingInterceptor,
  LoadingService,
  TenantSelectionService,
  FormShortUrlService,
  FormQrCodeService,
  APP_SETTINGS_TOKEN,
  RECAPTCHA_SITE_KEY,
  ORDER_TICKET_PUBLIC_FORMS_ORIGIN,
  PUBLIC_MARKETING_RUNTIME_CONFIG,
  type PublicMarketingRuntimeConfig,
} from '@crowdctrl/shared';
import { appThemeConfig } from '../theme.config';
import { AppSettingsService } from './services/app-settings.service';
import { OverviewLeaseService } from './services/overview-lease.service';
import { MyOrdersLeaseService } from './services/my-orders-lease.service';
import { CreateWorkspaceRouteReuseStrategy } from './create-workspace-route-reuse.strategy';

function initializeOrderService(): () => void {
  return () => {
    const orderService = inject(OrderService);
    orderService.setBaseUrl(environment.apiUrl);
  };
}

function initializePwaUpdateService(): () => void {
  return () => {
    const pwaUpdateService = inject(PwaUpdateService);
    pwaUpdateService.initialize();
  };
}

function initializeUserService(): () => void {
  return () => {
    const userService = inject(UserService);
    userService.setBaseUrl(environment.apiUrl);
  };
}

function initializeTenantService(): () => void {
  return () => {
    const tenantService = inject(TenantService);
    tenantService.setBaseUrl(environment.apiUrl);
  };
}

function initializeWorkspaceDefinitionService(): () => void {
  return () => {
    const workspaceDefinitionService = inject(WorkspaceDefinitionService);
    workspaceDefinitionService.setBaseUrl(environment.apiUrl);
  };
}

function initializeWorkspaceInstanceService(): () => void {
  return () => {
    const workspaceInstanceService = inject(WorkspaceInstanceService);
    workspaceInstanceService.setBaseUrl(environment.apiUrl);
  };
}

function initializeWorkspaceSessionService(): () => void {
  return () => {
    const workspaceSessionService = inject(WorkspaceSessionService);
    workspaceSessionService.setBaseUrl(environment.apiUrl);
  };
}

function initializeOverviewLeaseService(): () => void {
  return () => {
    const overviewLeaseService = inject(OverviewLeaseService);
    overviewLeaseService.setBaseUrl(environment.apiUrl);
  };
}

function initializeMyOrdersLeaseService(): () => void {
  return () => {
    const myOrdersLeaseService = inject(MyOrdersLeaseService);
    myOrdersLeaseService.setBaseUrl(environment.apiUrl);
  };
}

function initializeSessionMemberService(): () => void {
  return () => {
    const sessionMemberService = inject(SessionMemberService);
    sessionMemberService.setBaseUrl(environment.apiUrl);
  };
}

function initializeSessionMessageService(): () => void {
  return () => {
    const sessionMessageService = inject(SessionMessageService);
    sessionMessageService.setBaseUrl(environment.apiUrl);
  };
}

function initializeWorkspaceMemberService(): () => void {
  return () => {
    const workspaceMemberService = inject(WorkspaceMemberService);
    workspaceMemberService.setBaseUrl(environment.apiUrl);
  };
}

function initializeTenantSelectionService(): () => void {
  return () => {
    const tenantSelectionService = inject(TenantSelectionService);
    tenantSelectionService.setBaseUrl(environment.apiUrl);
  };
}

function initializeFormShortUrlService(): () => void {
  return () => {
    const formShortUrlService = inject(FormShortUrlService);
    formShortUrlService.setBaseUrl(environment.apiUrl);
  };
}

function initializeFormQrCodeService(): () => void {
  return () => {
    const formQrCodeService = inject(FormQrCodeService);
    formQrCodeService.setBaseUrl(environment.apiUrl);
  };
}

function initializeThemeService(): () => void {
  return () => {
    const themeService = inject(ThemeService);
    // Set default theme for this app
    const savedTheme = localStorage.getItem('crowdctrl-theme');
    if (!savedTheme) {
      themeService.setTheme(appThemeConfig.defaultTheme);
    }
  };
}

function initializeConfigService(): () => void {
  return async () => {
    const injector = inject(Injector);
    const configService = inject(ConfigService);
    try {
      // Get Remote Config instance
      const remoteConfig = inject(RemoteConfig);
      await configService.initializeWithRemoteConfig(remoteConfig);
    } catch (error) {
      // Fallback to defaults if Remote Config not available
      console.warn('Remote Config not available, using defaults:', error);
      await configService.initialize();
    }

    // Initialize tenant config service with API URL
    // Use runInInjectionContext since we're in an async callback
    runInInjectionContext(injector, () => {
      const tenantConfigService = inject(TenantConfigService);
      tenantConfigService.setBaseUrl(environment.apiUrl);
    });
  };
}

// Check if running on localhost (disable service worker only on localhost)
function isLocalhost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'en-AU' },
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: RouteReuseStrategy, useClass: CreateWorkspaceRouteReuseStrategy },
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    // MD-style stack transitions: less “card + heavy shadow” overlap than default iOS when
    // navigating (e.g. About/Contact → Login). Not from login lifecycle: ion-router-outlet animation.
    provideIonicAngular({
      navAnimation: mdTransitionAnimation,
    }),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: () => {
        const loadingService = inject(LoadingService);
        return new LoadingInterceptor(loadingService);
      },
      multi: true,
      deps: [LoadingService],
    },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideRemoteConfig(() => getRemoteConfig()),
    provideStorage(() => getStorage()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isLocalhost(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializePwaUpdateService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeUserService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTenantService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeWorkspaceDefinitionService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeWorkspaceInstanceService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeWorkspaceSessionService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeOverviewLeaseService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMyOrdersLeaseService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeOrderService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSessionMemberService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSessionMessageService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeWorkspaceMemberService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTenantSelectionService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFormShortUrlService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFormQrCodeService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeThemeService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeConfigService,
      multi: true,
    },
    {
      provide: APP_SETTINGS_TOKEN,
      useExisting: AppSettingsService,
    },
    {
      provide: RECAPTCHA_SITE_KEY,
      useValue: environment.recaptchaSiteKey ?? '',
    },
    {
      provide: ORDER_TICKET_PUBLIC_FORMS_ORIGIN,
      useValue: environment.formsPublicBaseUrl ?? '',
    },
    {
      provide: PUBLIC_MARKETING_RUNTIME_CONFIG,
      useValue: {
        apiUrl: (environment.apiUrl ?? '').trim(),
        appName: environment.appName,
        appSubtitle: environment.appSubtitle,
        contactEmail: (environment.contactEmail ?? '').trim(),
        registerInterestUrl: (environment.registerInterestUrl ?? '').trim(),
        environmentName: environment.name,
        production: environment.production,
      } satisfies PublicMarketingRuntimeConfig,
    },
  ],
};
